import { z } from "zod";
import { ROUTES } from "@lib/routes";
import { MODALITIES } from "@lib/learner/modality/modality";
import type { PreferenceField } from "@lib/userPreferencesSchema";

/**
 * In-band agent action protocol (D-13): the tutor stream may end with a
 * `[ACTION{...json}]` token proposing an action. Mirrors the `endToken.ts`
 * `[CONVERSATION_END]` precedent - the LLM can only emit text; nothing
 * executes until the learner taps the confirm chip, whose fetch hits the
 * same authenticated routes the on-page controls use (D-14).
 */

/** Navigation whitelist: the model proposes a key, never a raw path. */
export const NAVIGATE_DESTINATIONS = {
  home: ROUTES.home,
  board: ROUTES.curriculum,
  history: ROUTES.curriculumHistory,
  sessions: ROUTES.sessions,
  scenarios: ROUTES.scenarios,
  settings: ROUTES.settings,
  progress: ROUTES.progress,
  review: ROUTES.review,
  profile: ROUTES.profile,
} as const;

export type NavigateDestination = keyof typeof NAVIGATE_DESTINATIONS;

const destinationKeys = Object.keys(NAVIGATE_DESTINATIONS) as [
  NavigateDestination,
  ...NavigateDestination[],
];

/** Lesson-runner deep link, as copied from page data (board/history hrefs). */
const LESSON_HREF_RE = /^\/curriculum\/\d+\/\d+\/\d+\/\d+(\?content=\d+)?$/;

/**
 * Low-risk preferences the tutor may propose changing. Locales and the
 * learner-context tier are deliberately excluded. Values are only
 * shape-checked here; the settings PATCH route's zod schema is the real
 * validator.
 */
export const SETTABLE_PREFERENCES = {
  passageAudioSpeed: "number between 0.5 and 1.2",
  tutorSpeechSpeed: "number between 0.5 and 1.5",
  autoplayTutorAudio: "boolean",
  reducedMotion: "boolean",
  feedbackVerbosity: '"BRIEF" or "DETAILED"',
  vocabRevealMode: '"TAP" or "ALWAYS"',
  passageFontScale: '"SM", "MD", or "LG"',
} as const satisfies Partial<Record<PreferenceField, string>>;

export type SettablePreferenceKey = keyof typeof SETTABLE_PREFERENCES;

const preferenceKeys = Object.keys(SETTABLE_PREFERENCES) as [
  SettablePreferenceKey,
  ...SettablePreferenceKey[],
];

const modalitySchema = z.enum(MODALITIES);

const actionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("regenerate_slot"),
    modality: modalitySchema,
  }),
  z.object({
    type: z.literal("create_subject_lesson"),
    modality: modalitySchema,
    subject: z.string().min(1).max(200),
  }),
  z.object({
    type: z.literal("navigate"),
    destination: z.enum(destinationKeys),
    reason: z.string().max(200).optional(),
  }),
  z.object({
    type: z.literal("open_lesson"),
    href: z.string().regex(LESSON_HREF_RE),
    title: z.string().max(200).optional(),
  }),
  z.object({
    type: z.literal("set_preference"),
    key: z.enum(preferenceKeys),
    value: z.union([z.string().max(40), z.number(), z.boolean()]),
  }),
  z.object({
    type: z.literal("report_issue"),
    summary: z.string().min(1).max(500),
  }),
]);

export type AgentAction = z.infer<typeof actionSchema>;

// [\s\S] instead of the `s` (dotAll) flag: container tsc targets pre-es2018.
const TOKEN_RE = /\[ACTION(\{[\s\S]*?\})\]\s*$/;

/**
 * Strip a trailing action token from tutor text. Malformed JSON, unknown
 * types, or invalid payloads drop silently - the tip text still renders,
 * no chip (research A4).
 */
export function parseActionToken(text: string): { display: string; action: AgentAction | null } {
  const m = text.match(TOKEN_RE);
  if (!m) return { display: text, action: null };
  const display = text.slice(0, m.index).trimEnd();
  try {
    const parsed = actionSchema.safeParse(JSON.parse(m[1]));
    return { display, action: parsed.success ? parsed.data : null }; // malformed → drop silently
  } catch {
    return { display, action: null };
  }
}

export type ActionInstructionContext = {
  /** Per-modality levels line (same format as the tutor-context "Skill levels:" line). */
  skillLevelsLine: string;
  /** Current page kind from the page context ("lesson" when in a lesson). */
  currentPage?: string | null;
  /** Advertise open_lesson only when the page data carries lesson hrefs. */
  hasLessonHrefs?: boolean;
};

/**
 * System-prompt block teaching the tutor the available actions and the
 * emission format, tailored to what the current page makes possible.
 */
export function buildActionInstructions(ctx: ActionInstructionContext): string {
  const destinations = destinationKeys
    .map((key) => (key === ctx.currentPage ? `${key} (current page)` : key))
    .join(", ");
  const preferences = preferenceKeys
    .map((key) => `${key} (${SETTABLE_PREFERENCES[key]})`)
    .join("; ");

  return [
    "Actions you can propose (the learner confirms with a tap; nothing runs otherwise):",
    ctx.skillLevelsLine ? `Current skill levels: ${ctx.skillLevelsLine}` : null,
    "- regenerate_slot: regenerate the learner's board lesson for a modality (READING, WRITING, SPEAKING, LISTENING) at their current level.",
    "- create_subject_lesson: create a board lesson about a specific subject for a modality.",
    `- navigate: take the learner to an app page. Destinations: ${destinations}.`,
    ctx.hasLessonHrefs
      ? "- open_lesson: open a specific lesson using an href copied EXACTLY from the page data (lessonHrefs)."
      : null,
    `- set_preference: change one setting for the learner. Keys: ${preferences}.`,
    "- report_issue: when the learner describes something broken or clearly wrong with the app (not a language question), propose filing a report with a one-sentence summary of the problem.",
    "To propose an action, end your reply with the bracketed token on its own line.",
    "Propose at most ONE action, and only when the learner asks for it or it clearly helps; otherwise reply normally with no token.",
    'Never propose an action in reply to a greeting, a thank-you, a clarifying message like "what?", or a complaint about your replies; just respond with no token.',
    "Examples:",
    '[ACTION{"type":"regenerate_slot","modality":"WRITING"}]',
    '[ACTION{"type":"create_subject_lesson","modality":"READING","subject":"ordering food at a restaurant"}]',
    '[ACTION{"type":"navigate","destination":"review","reason":"You have cards due for review"}]',
    ctx.hasLessonHrefs
      ? '[ACTION{"type":"open_lesson","href":"/curriculum/1/2/3/42","title":"No mercado"}]'
      : null,
    '[ACTION{"type":"set_preference","key":"passageAudioSpeed","value":0.75}]',
    '[ACTION{"type":"report_issue","summary":"Passage audio fails to play on the reading lesson"}]',
  ]
    .filter((line): line is string => line !== null)
    .join("\n");
}
